import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldOff } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import Button from '../../../../components/ui/Button';
import { resetUserMfa } from '../../../../api/General/Mfa';

// CO-323 — HR resets an employee's two-factor authentication (lost phone / new device).
// HR-only surface (rendered by Personal/index.tsx for HR viewing another employee). The server enforces
// same-company and refuses Operator targets; the reset also signs the employee out everywhere.
// Layout mirrors DataExportSection so the Personal tab reads as one family of cards.

interface Props {
    userId: string;
}

const MfaResetSection: React.FC<Props> = ({ userId }) => {
    const { t } = useTranslation();
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    const onReset = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await resetUserMfa(userId);
            if (res.errors?.length) {
                setError(res.errors[0]?.description ?? t('mfa.reset.failed'));
                return;
            }
            setDone(true);
            setConfirming(false);
        } catch {
            setError(t('mfa.reset.failed'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card data-testid="mfa-reset-card">
            <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:justify-between">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
                            <ShieldOff className="w-5 h-5 text-primary" aria-hidden="true" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold">{t('mfa.reset.title')}</h2>
                            <p className="text-sm text-muted-foreground">{t('mfa.reset.description')}</p>
                        </div>
                    </div>
                    {!confirming && (
                        <div className="flex flex-wrap gap-2 sm:justify-end shrink-0">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setConfirming(true); setDone(false); setError(null); }}
                                data-testid="mfa-reset-start"
                            >
                                {t('mfa.reset.action')}
                            </Button>
                        </div>
                    )}
                </div>

                <div className="mt-4" aria-live="polite">
                    {confirming && (
                        <div
                            className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between p-3 rounded-md border border-border bg-muted/40"
                            data-testid="mfa-reset-confirm"
                        >
                            <p className="text-sm text-foreground">{t('mfa.reset.confirmPrompt')}</p>
                            <div className="flex flex-wrap gap-2 shrink-0">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={busy}
                                    onClick={() => setConfirming(false)}
                                    data-testid="mfa-reset-cancel"
                                >
                                    {t('mfa.reset.cancel')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="destructive"
                                    loading={busy}
                                    onClick={onReset}
                                    data-testid="mfa-reset-confirm-button"
                                >
                                    {t('mfa.reset.confirm')}
                                </Button>
                            </div>
                        </div>
                    )}

                    {done && (
                        <p className="text-sm text-emerald-700" data-testid="mfa-reset-done">{t('mfa.reset.done')}</p>
                    )}

                    {error && (
                        <div className="mt-3 p-3 rounded-md text-sm bg-rose-50 text-rose-700 border border-rose-200" role="alert" data-testid="mfa-reset-error">
                            {error}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default MfaResetSection;
